import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";

const POLL_INTERVAL = 2000;
const POLL_MAX = 15; // ~30s

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | timeout | error
  const [attempts, setAttempts] = useState(0);
  const { refresh } = useAuth();
  const stopped = useRef(false);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let n = 0;
    const tick = async () => {
      if (stopped.current) return;
      n += 1;
      setAttempts(n);
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          stopped.current = true;
          await refresh();
          setState("paid");
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") {
          stopped.current = true;
          setState("error");
          return;
        }
      } catch {
        // keep trying
      }
      if (n >= POLL_MAX) {
        stopped.current = true;
        setState("timeout");
        return;
      }
      setTimeout(tick, POLL_INTERVAL);
    };
    tick();
    return () => { stopped.current = true; };
     
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#05050A] text-white grid place-items-center px-6">
      <div className="max-w-md w-full text-center p-8 md:p-10 glass rounded-3xl border border-white/10">
        {state === "polling" && (
          <>
            <Loader2 className="mx-auto text-[#00E5FF] animate-spin" size={48} />
            <h1 className="font-[Unbounded] text-2xl mt-6">Confirming your signal…</h1>
            <p className="text-white/60 mt-3">Waiting for Stripe to finalize the payment. Attempt {attempts}/{POLL_MAX}.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="mx-auto text-[#39FF14]" size={48} />
            <h1 className="font-[Unbounded] text-2xl mt-6">You&rsquo;re Pro</h1>
            <p className="text-white/60 mt-3">Radar range extended and deeper AI reasoning unlocked. Your account has been upgraded.</p>
            <Link data-testid="pay-success-back-btn" to="/radar" className="inline-flex items-center gap-2 mt-8 bg-[#00E5FF] text-black font-semibold px-6 py-3 rounded-full hover:glow-primary transition-all">
              Enter the grid <ArrowRight size={16} />
            </Link>
          </>
        )}
        {state === "timeout" && (
          <>
            <Loader2 className="mx-auto text-white/40" size={48} />
            <h1 className="font-[Unbounded] text-2xl mt-6">Still processing</h1>
            <p className="text-white/60 mt-3">Stripe is taking longer than usual. Your tier will update automatically as soon as the payment settles.</p>
            <Link to="/profile" className="inline-flex mt-8 text-[#00E5FF] hover:underline">Back to profile</Link>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle className="mx-auto text-[#FF007F]" size={48} />
            <h1 className="font-[Unbounded] text-2xl mt-6">Payment not confirmed</h1>
            <p className="text-white/60 mt-3">We couldn&rsquo;t verify this checkout session. If you were charged, contact support.</p>
            <Link to="/profile" className="inline-flex mt-8 text-[#00E5FF] hover:underline">Back to profile</Link>
          </>
        )}
      </div>
    </div>
  );
}
